import React from "react";
import { Typography } from "@mui/material";
import { toast } from "react-toastify";
import CustomDialog from "../CustomDialog";
import CustomButton from "../CustomButton";
import CustomTextField from "../CustomTextField";
import CustomLabel from "../CustomLabel";
import { cancelSession } from "../../services/ClassesService";
import { getErrorMessage } from "../../utils/apiResponse";
import { formatTableDate } from "../../utils/tableDateFormat";
import { ClassSession } from "./types";
import { formatTime } from "./classUtils";

interface CancelClassSessionDialogProps {
  open: boolean;
  session: ClassSession | null;
  onClose: () => void;
  onCancelled: () => void;
}

/**
 * Cancels a whole class session. The session flips to CANCELLED even if some
 * attendee's billing cleanup fails, so failures are surfaced separately instead
 * of being treated as an error.
 */
const CancelClassSessionDialog: React.FC<CancelClassSessionDialogProps> = ({
  open,
  session,
  onClose,
  onCancelled,
}) => {
  const [reason, setReason] = React.useState("");
  const [cancelling, setCancelling] = React.useState(false);

  React.useEffect(() => {
    if (open) setReason("");
  }, [open]);

  const handleConfirm = async () => {
    if (!session?.id) return;
    setCancelling(true);
    try {
      const result = await cancelSession(
        String(session.id),
        reason.trim() || undefined
      );

      const cancelledCount = result?.cancelled_attendees?.length ?? 0;
      const failures = result?.failures?.length ?? 0;

      toast.success(
        `Session cancelled — ${cancelledCount} attendee${
          cancelledCount === 1 ? "" : "s"
        } refunded.`
      );
      if (failures > 0) {
        toast.warn(
          `${failures} attendee${
            failures === 1 ? "" : "s"
          } could not be refunded automatically — check them manually.`
        );
      }

      onCancelled();
      onClose();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to cancel the session."));
    } finally {
      setCancelling(false);
    }
  };

  return (
    <CustomDialog
      open={open}
      onClose={() => {
        if (!cancelling) onClose();
      }}
      title="Cancel session"
      actions={
        <>
          <CustomButton
            label="Keep session"
            variant="outlined"
            onClick={onClose}
            disabled={cancelling}
          />
          <CustomButton
            label="Cancel session"
            variant="contained"
            bgColor="#d32f2f"
            textColor="#fff"
            loading={cancelling}
            disabled={cancelling}
            onClick={handleConfirm}
          />
        </>
      }
    >
      <Typography sx={{ mb: 2, fontSize: 14 }}>
        {`This cancels the whole session on ${formatTableDate(
          session?.start_at
        )} at ${formatTime(
          session?.start_at
        )}. Every booked attendee gets their package session credited back or their invoice cancelled.`}
      </Typography>
      <CustomLabel text="Reason (optional)" fontSize={14} fontWeight={500} />
      <CustomTextField
        value={reason}
        onChange={(e: any) => setReason(e.target.value)}
        fullWidth
      />
    </CustomDialog>
  );
};

export default CancelClassSessionDialog;
